import { useCallback, useEffect, useRef, useState } from 'react';
import { peekWidgetCache, widgetFetch, invalidateWidgetCache, WIDGET_CACHE_TTL_MS } from '../lib/widget-cache';

/**
 * 위젯 데이터 로딩 표준 훅 — `widget-cache` 위의 얇은 껍데기.
 *
 * 기존 대시보드 위젯은 전부 아래 6줄을 복사해 쓰고 있었다:
 *   const [data,setData]=useState(); const [loading,setLoading]=useState(true);
 *   const [error,setError]=useState(null);
 *   useEffect(()=>{ void (async()=>{ try{...}catch{...}finally{setLoading(false)} })() },[])
 * 위젯이 12종으로 늘면 같은 코드가 12벌이 되고, 언마운트 후 setState 가드도 매번 빠진다.
 * 본 훅이 그 자리를 대신한다 — 위젯은 body 렌더에만 집중한다.
 *
 * 특징:
 *   - 캐시 히트면 **동기 시드** → 보드 탭 전환 시 스피너가 뜨지 않는다.
 *   - loader 는 ref 로 잡아둔다 → 인라인 화살표를 넘겨도 재요청이 폭주하지 않는다.
 *     (재요청 트리거는 오직 key · enabled · reload())
 *   - 언마운트 후 setState 금지 가드 내장.
 */

export interface WidgetDataState<T> {
  data: T | undefined;
  loading: boolean;
  error: string | null;
  /** 캐시를 버리고 다시 불러온다. */
  reload: () => void;
}

export interface UseWidgetDataOptions {
  /** 캐시 유효시간(ms). 기본 60초. */
  ttlMs?: number;
  /** false 면 요청하지 않는다 (예: 로그인 전, 권한 없음). */
  enabled?: boolean;
}

export function useWidgetData<T>(
  key: string,
  loader: () => Promise<T>,
  options: UseWidgetDataOptions = {},
): WidgetDataState<T> {
  const { ttlMs = WIDGET_CACHE_TTL_MS, enabled = true } = options;

  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const [nonce, setNonce] = useState(0);
  const seed = enabled ? peekWidgetCache<T>(key, ttlMs) : undefined;
  const [data, setData] = useState<T | undefined>(seed);
  const [loading, setLoading] = useState<boolean>(enabled && seed === undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    // 캐시 히트는 동기 반영 — 렌더 1프레임도 스피너를 보이지 않는다.
    const cached = peekWidgetCache<T>(key, ttlMs);
    if (cached !== undefined) {
      setData(cached);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    widgetFetch<T>(key, () => loaderRef.current(), ttlMs)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError((err as Error)?.message ?? '조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [key, ttlMs, enabled, nonce]);

  const reload = useCallback(() => {
    invalidateWidgetCache(key);
    setNonce((n) => n + 1);
  }, [key]);

  return { data, loading, error, reload };
}
